import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material'
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridColumnVisibilityModel,
  GridRowGroupingModel,
} from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useLinkedServiceSelection } from 'src/v3/manage/hooks/use-linked-service-selection'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  activeLinkedServiceChanged,
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
  fetchGroupsV2,
  groupsV2FiltersChanged,
  selectGroupsV2,
  selectGroupsV2Status,
  selectGroupsV2Filters,
} from '../manage.slice'
import {
  GROUPS_URLS,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../constants'
import { deleteGroup } from '../utils/api'
import { dataGridSx } from '../styles'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import { GroupsV2Row, GroupsV2GroupingView } from './types'

let groupingModels: Record<GroupsV2GroupingView, GridRowGroupingModel> = {
  group: ['groupName'],
  user: ['userName'],
}

// Aggregation: first non-null value in the group (e.g. groupCreatedTime is same for all rows in a group)
let firstValueAggregation: GridAggregationFunction<
  string | null,
  string | null
> = {
  apply: ({ values }) => values.find((v) => v != null) ?? null,
  label: '',
  hasCellUnit: false,
}

let groupsV2AggregationFunctions = {
  firstValue: firstValueAggregation,
}

let aggregationModel = {
  groupCreatedTime: 'firstValue',
}

export default function GroupsV2(): ReactElement {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let location = useLocation()
  let linkedServiceID = useLinkedServiceSelection()

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let filters = useAppSelector(selectGroupsV2Filters)
  let rows = useAppSelector(selectGroupsV2(linkedServiceID))
  let dataStatus = useAppSelector(selectGroupsV2Status(linkedServiceID))

  let [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  let [isDeleting, setIsDeleting] = useState(false)

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (!linkedServiceID || linkedServiceID === 'unselected') return
    if (dataStatus === 'uninitialized') {
      dispatch(fetchGroupsV2(linkedServiceID))
    }
  }, [linkedServiceID, dataStatus, dispatch])

  let isLinkedServicesLoaded =
    linkedServicesStatus === 'fulfilled' || linkedServicesStatus === 'rejected'
  let loading =
    (dataStatus === 'uninitialized' || dataStatus === 'pending') &&
    !!linkedServiceID &&
    linkedServiceID !== 'unselected'

  // Map from groupName → groupID for looking up group ID in group rows
  let groupNameToIdMap = useMemo<Record<string, string>>(
    () => Object.fromEntries(rows.map((row) => [row.groupName, row.groupID])),
    [rows],
  )

  let summaryStats = useMemo(() => {
    let uniqueGroups = new Set(rows.map((r) => r.groupID)).size
    let uniqueUsers = new Set(rows.map((r) => r.userID)).size
    let avgUsersPerGroup =
      uniqueGroups > 0 ? (rows.length / uniqueGroups).toFixed(1) : '0'
    return { uniqueGroups, uniqueUsers, avgUsersPerGroup }
  }, [rows])

  let handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    await deleteGroup(deleteTarget.id)
      .then(() => dispatch(fetchGroupsV2(linkedServiceID)))
      .then(() =>
        showSuccess(`Group "${deleteTarget.name}" deleted successfully.`),
      )
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error deleting group:', deleteTarget.id, err)
        showError(`Error deleting group "${deleteTarget.name}".`)
      })
      .finally(() => {
        setIsDeleting(false)
        setDeleteTarget(null)
      })
  }, [deleteTarget, linkedServiceID, dispatch, showSuccess, showError])

  let handleViewChange = useCallback(
    (_: unknown, value: GroupsV2GroupingView | null) => {
      if (value) dispatch(groupsV2FiltersChanged({ groupingView: value }))
    },
    [dispatch],
  )

  let MainToolbar = useCallback(
    () => <DataGridToolbar fileName="groups-v2" />,
    [],
  )

  let groupedFields = groupingModels[filters.groupingView]

  let columnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      ...filters.columnVisibility,
      ...Object.fromEntries(groupedFields.map((f) => [f, false])),
      groupActions: filters.groupingView === 'group',
    }),
    [filters.columnVisibility, groupedFields, filters.groupingView],
  )

  let handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      let controlled = [...groupedFields, 'groupActions']
      let userPrefs = Object.fromEntries(
        Object.entries(model).filter(([f]) => !controlled.includes(f)),
      )
      dispatch(groupsV2FiltersChanged({ columnVisibility: userPrefs }))
    },
    [groupedFields, dispatch],
  )

  let columns = useMemo<GridColDef<GroupsV2Row>[]>(
    () => [
      {
        field: 'groupName',
        headerName: 'Group',
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: 'userName',
        headerName: 'User',
        flex: 1.5,
        minWidth: 160,
      },
      {
        field: 'userEmail',
        headerName: 'Email',
        flex: 2,
        minWidth: 200,
      },
      {
        field: 'licenseGroups',
        headerName: 'License Group',
        flex: 1,
        minWidth: 140,
        valueFormatter: (value: string[]) => value?.join(', ') ?? '',
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              License Group
            </Typography>
            <Tooltip title="The license group(s) this user holds. When scheduling an assignment using auto-groups, all users in the selected license group are automatically included.">
              <InfoOutlinedIcon
                sx={{ fontSize: 15, color: 'text.secondary' }}
              />
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'groupCreatedTime',
        headerName: 'Group Created',
        width: 150,
        aggregable: true,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).format('MMM D, YYYY') : null,
      },
      {
        field: 'groupActions',
        headerName: 'Actions',
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.rowNode.type !== 'group') return null
          let groupName = params.rowNode.groupingKey as string
          let groupID = groupNameToIdMap[groupName]
          if (!groupID) return null
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                gap: 0.5,
              }}
            >
              <Tooltip title="Edit group">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`${GROUPS_URLS.INDEX}/${groupID}/edit`, {
                      state: { edit: true },
                    })
                  }}
                  aria-label={`Edit ${groupName}`}
                >
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete group">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteTarget({ id: groupID, name: groupName })
                  }}
                  aria-label={`Delete ${groupName}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
      },
    ],
    [groupNameToIdMap, navigate],
  )

  // Handle success message from navigation state (after create/edit)
  useEffect(() => {
    let state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      showSuccess(state.successMessage)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate, showSuccess])

  return (
    <Container maxWidth="xl" className="py-6">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Groups
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          disabled={!linkedServiceID || linkedServiceID === 'unselected'}
          onClick={() =>
            navigate(GROUPS_URLS.CREATE, { state: { linkedServiceID } })
          }
          aria-label="Create new group"
        >
          Create Group
        </Button>
      </Box>
      {/* Summary metrics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          label={`${summaryStats.uniqueGroups} ${summaryStats.uniqueGroups === 1 ? 'group' : 'groups'}`}
          color="primary"
        />
        <Chip
          label={`${summaryStats.uniqueUsers} ${summaryStats.uniqueUsers === 1 ? 'user' : 'users'}`}
          color="secondary"
        />
        <Chip
          label={`${summaryStats.avgUsersPerGroup} avg users/group`}
          variant="outlined"
        />
      </Box>
      {/* Filters card */}
      {isLinkedServicesLoaded ? (
        <Card className="p-6 mb-6" sx={{ bgcolor: 'background.paper' }}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              alignItems: 'center',
            }}
          >
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id="linked-service-label">Linked Service</InputLabel>
              <Select
                labelId="linked-service-label"
                label="Linked Service"
                value={linkedServiceID || ''}
                onChange={(e) =>
                  dispatch(activeLinkedServiceChanged(e.target.value))
                }
                displayEmpty
                sx={{ bgcolor: 'background.paper' }}
              >
                {linkedServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                View by:
              </Typography>
              <ToggleButtonGroup
                value={filters.groupingView}
                exclusive
                onChange={handleViewChange}
                size="small"
              >
                <ToggleButton value="group">By Group</ToggleButton>
                <ToggleButton value="user">By User</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Card>
      ) : (
        <Card
          className="p-6 mb-6"
          sx={{ bgcolor: 'background.paper' }}
          role="status"
          aria-busy="true"
          aria-label="Loading filters"
        >
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            }}
          >
            <Skeleton
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Card>
      )}
      {/* Data grid card */}
      <Card className="p-6">
        {loading ? (
          <Skeleton
            variant="rectangular"
            height={480}
            sx={{ borderRadius: 2 }}
          />
        ) : !linkedServiceID || linkedServiceID === 'unselected' ? (
          <Box className="py-12 text-center" role="status">
            <Typography variant="body1" color="text.secondary">
              Please select a linked service to view groups
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box className="py-12 text-center" role="status">
            <Typography variant="body1" color="text.secondary">
              No groups found for this service
            </Typography>
          </Box>
        ) : (
          <DataGridPremium
            rows={rows}
            columns={columns}
            rowGroupingModel={groupedFields}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            defaultGroupingExpansionDepth={0}
            groupingColDef={{
              minWidth: 240,
              flex: 2,
              headerName: filters.groupingView === 'group' ? 'Group' : 'User',
            }}
            aggregationFunctions={{
              ...GRID_AGGREGATION_FUNCTIONS,
              ...groupsV2AggregationFunctions,
            }}
            aggregationModel={aggregationModel}
            getAggregationPosition={() => 'inline'}
            initialState={{
              pagination: {
                paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
              },
              pinnedColumns: { right: ['groupActions'] },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            pagination
            disableRowSelectionOnClick
            showToolbar
            slots={{ toolbar: MainToolbar }}
            sx={dataGridSx}
          />
        )}
      </Card>
      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        entityName="group"
        entityDisplayName={deleteTarget?.name ?? ''}
        isSubmitting={isDeleting}
      />
      {NotificationComponent}
    </Container>
  )
}
